import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { theme } from '../styles/theme';
import ByteBotLogo from './ByteBotLogo';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { resetPassword, error } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    
    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (error) {
      console.error('Password reset error:', error);
    }
    setLoading(false);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.background,
      fontFamily: "'Inter', sans-serif",
      padding: '20px',
    },
    card: {
      backgroundColor: theme.colors.surface,
      padding: '40px',
      borderRadius: theme.borderRadius.xl,
      border: `2px solid ${theme.colors.borderDark}`,
      maxWidth: '400px',
      width: '100%',
      textAlign: 'center',
    },
    header: {
      marginBottom: '30px',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    logoIcon: {
      marginRight: theme.spacing.sm,
      display: 'flex',
      alignItems: 'center',
    },
    logoAccent: {
      color: theme.colors.primary,
    },
    title: {
      fontSize: theme.typography.fontSizes['3xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.sm,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    subtitle: {
      fontSize: theme.typography.fontSizes.base,
      color: theme.colors.textLight,
      marginBottom: theme.spacing.lg,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.lg,
    },
    input: {
      padding: '15px',
      border: `2px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.base,
      fontFamily: "'Inter', sans-serif",
      backgroundColor: theme.colors.background,
      transition: 'all 0.2s ease',
      color: theme.colors.text,
    },
    inputFocus: {
      outline: 'none',
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.surface,
    },
    button: {
      padding: '15px',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      backgroundColor: theme.colors.primary,
      color: '#fff',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    error: {
      color: theme.colors.primary,
      textAlign: 'center',
      padding: theme.spacing.md,
      backgroundColor: theme.colors.primary + '10',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.sm,
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    success: {
      color: '#10b981',
      textAlign: 'center',
      padding: theme.spacing.md,
      backgroundColor: '#10b98110',
      border: '2px solid #10b981',
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.sm,
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    switchText: {
      textAlign: 'center',
      marginTop: theme.spacing.lg,
      color: theme.colors.textLight,
      fontSize: theme.typography.fontSizes.sm,
    },
    switchLink: {
      color: theme.colors.primary,
      textDecoration: 'none',
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    backLink: {
      display: 'inline-block',
      marginBottom: theme.spacing.lg,
      color: theme.colors.textLight,
      textDecoration: 'none',
      fontSize: theme.typography.fontSizes.sm,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <Link to="/login" style={styles.backLink}>← [BACK TO LOGIN]</Link>
        
        <div style={styles.header}>
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <ByteBotLogo 
                size={32} 
                color={theme.colors.primary}
              />
            </div>
            [BYTE<span style={styles.logoAccent}>BOT</span>]
          </div>
          <h1 style={styles.title}>[RESET PASSWORD]</h1>
          <p style={styles.subtitle}>
            {success 
              ? "Check your email for reset instructions."
              : "Enter your email address and we'll send you a reset link."
            }
          </p>
        </div>

        {error && (
          <div style={styles.error}>
            [ERROR] {error}
          </div>
        )}

        {success && (
          <div style={styles.success}>
            [SUCCESS] Password reset email sent! Check your inbox and spam folder.
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} style={styles.form}>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={styles.input}
              onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
              onBlur={(e) => Object.assign(e.target.style, styles.input)}
            />

            <button
              type="submit"
              disabled={loading}
              style={styles.button}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme.colors.primaryDark;
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = theme.colors.primary;
                e.target.style.transform = 'translateY(0)';
              }}
            >
              {loading ? '[SENDING...]' : '[SEND RESET EMAIL]'}
            </button>
          </form>
        )}

        <div style={styles.switchText}>
          Remember your password?{' '}
          <Link to="/login" style={styles.switchLink}>
            [BACK TO LOGIN]
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword; 